import React, { lazy, useCallback, useEffect, useRef, useState } from 'react';
import { get } from 'lodash';
import { useDrawerStore } from '../../../stores/drawerStore';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { useInteractionStore } from '../../../stores/interactionStore';
import { MENUS } from '../../my-central/constants';
import withSuspense from '../../../components/withLazyComponent';
import { UserSignUpResponse } from '../service';
import { trackingService } from '../../../shared/eventTracking';
import { Tracker } from '../../../shared/tracker/Tracker';
import { amplitudeModule } from '../../../shared/amplitude';
import { DrugInteractionsHandle } from '../../my-central/DrugInteractions';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { BottomSheet } from '../../../components/BottomSheet/BottomSheet';
import { SubPageFooter } from './SubPageFooter';
import FreeSampleSidebar from '../../../components/FreeSampleSidebar';
import { mapEstablishmentOptions, mapPrescriberInfo } from '../../../interfaces/freeSample';

import { ReactComponent as PatientDataIcon } from '../../../assets/images/icons/patient_data.svg';
import { ReactComponent as HistoryIcon } from '../../../assets/images/icons/history.svg';
import { ReactComponent as InteractionsIcon } from '../../../assets/images/icons/interactions.svg';
import { ReactComponent as DoctorIcon } from '../../../assets/images/icons/doctor.svg';
import { ReactComponent as HelpIcon } from '../../../assets/images/icons/help.svg';
import { ReactComponent as MevoAppIcon } from '../../../assets/images/icons/mevo/ic-cell-phone.svg';
import { ReactComponent as GiftIcon } from '../../../assets/images/icons/free-sample-gift.svg';
import { ReactComponent as CalendarClockIcon } from '../../../assets/images/icons/ic-calendar-clock.svg';
import { ReactComponent as SncrNumberingIcon } from '../../../assets/images/icons/hash.svg';
import { appViewportHeight } from '../../../helpers/embeddedViewport';

const PatientData = withSuspense(lazy(() => import('../../my-central/PatientData')));
const DrugInteractions = withSuspense(lazy(() => import('../../my-central/DrugInteractions')));
const Help = withSuspense(lazy(() => import('../../my-central/Help')));
const History = withSuspense(lazy(() => import('../../my-central/History/History')));
const PrescriberCentral = withSuspense(lazy(() => import('../../my-central/Prescriber/PrescriberCentral')));
const Office = withSuspense(lazy(() => import('../../my-central/Office')));
const Policy = withSuspense(lazy(() => import('../../my-central/Policy')));
const Recurrences = withSuspense(lazy(() => import('../../my-central/Recurrences/Recurrences').then(m => ({ default: m.Recurrences }))));

interface HamburgerMenuPanelProps {
  canUsePrescriptionHistory: boolean;
  userSignUpStatus?: UserSignUpResponse | null;
}

type MenuKey = typeof MENUS[keyof typeof MENUS] | '';

const MENU_LABELS: Partial<Record<MENUS, string>> = {
  [MENUS.PATIENT_DATA]: 'Perfil do paciente',
  [MENUS.DRUG_INTERACTIONS]: 'Interações',
  [MENUS.PERSONAL_SETTINGS]: 'Configurações',
  [MENUS.POLICY]: 'Política',
  [MENUS.HELP]: 'Ajuda',
  [MENUS.HISTORY]: 'Histórico',
  [MENUS.PRESCRIBER_DATA]: 'Dados do prescritor',
  [MENUS.News]: 'Novidades',
  [MENUS.OFFICE]: 'Mevo App',
};

const COLOR_PRIMARY = 'var(--color-primary)';

function CloseBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label="Fechar"
      className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors cursor-pointer bg-transparent border-0 p-0"
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M1 1L13 13M13 1L1 13" style={{ stroke: 'var(--color-gray-dark)' }} strokeWidth="2" strokeLinecap="round" />
      </svg>
    </button>
  );
}

function ChevronRight() {
  return (
    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" className="shrink-0">
      <path d="M1 1L6 6L1 11" style={{ stroke: 'var(--color-gray-medium)' }} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SubPageHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between px-4 py-4 border-b border-gray-lighter bg-white sticky top-0 z-10 shrink-0">
      <p className="text-[15px] font-bold text-ink m-0">{title}</p>
      <CloseBtn onClick={onClose} />
    </div>
  );
}

function MenuItemRow({ icon, label, onClick, badge, noDivider }: {
  icon: React.ReactNode; label: string; onClick: () => void; badge?: number; noDivider?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-4 py-[10px] ${noDivider ? '' : 'border-b border-gray-lightest'} hover:bg-gray-50 transition-colors cursor-pointer bg-transparent border-l-0 border-r-0 border-t-0 text-left`}
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-[10px] bg-primary-soft flex items-center justify-center shrink-0">
          <span className="flex" style={{ fill: COLOR_PRIMARY, color: COLOR_PRIMARY }}>{icon}</span>
        </div>
        <span className="text-[14px] text-gray-dark font-bold">{label}</span>
        {badge !== undefined && badge > 0 && (
          <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      <ChevronRight />
    </button>
  );
}

export default function HamburgerMenuPanel({ canUsePrescriptionHistory, userSignUpStatus }: HamburgerMenuPanelProps) {
  const { drawerOpen, setDrawerOpen, selectedMenu, setSelectedMenu, officeTriggerSource } = useDrawerStore();
  const { medicineDrugInteractions, medicineId, numberOfInteractions, interactionId } = useInteractionStore();
  const medicines = usePrescriptionStore((s) => s.prescription?.Itens);
  const viewByActivePrinciple = usePrescriptionStore((s) => s.prescription?.ExibirPorPrincipioAtivo);
  const prescriber = usePrescriptionStore((s) => s.prescriber());
  const establishment = usePrescriptionStore((s) => s.prescription?.Estabelecimento);
  const prescriberConfiguration = usePrescriptionStore((s) => s.prescriberConfiguration);
  const isSncrNumberingEnabled = get(prescriberConfiguration, 'Features.enableSNCR', false);
  const establishmentName = usePrescriptionStore((s) => s.prescription?.Estabelecimento?.Nome);
  const isMobile = useIsMobile();

  const enableFreeSample = get(prescriberConfiguration, 'Features.enableFreeSample', false);

  const [isFreeSampleOpen, setIsFreeSampleOpen] = useState(false);
  const freeSampleEstablishmentOptions = mapEstablishmentOptions(establishment);

  const overlayRef = useRef<HTMLDivElement>(null);
  const drugInteractionsRef = useRef<DrugInteractionsHandle>(null);

  const closePanel = useCallback(() => {
    setDrawerOpen(false);
    setSelectedMenu('');
  }, [setDrawerOpen, setSelectedMenu]);

  const trackMenuOpen = useCallback((menu: MenuKey) => {
    const menuLabel = MENU_LABELS[menu as MENUS] ?? String(menu);
    amplitudeModule.menuClicked({
      screen_name: 'prescrição',
      name: menuLabel,
      item: [{ id: 0, name: menuLabel }],
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
      establishment_name: establishmentName ?? '',
    });

    switch (menu) {
      case MENUS.PATIENT_DATA:
        Tracker.trackEvent({ event: 'Open Patient Info Central' });
        break;
      case MENUS.DRUG_INTERACTIONS:
        Tracker.trackEvent({ event: 'Open Interactions Central' });
        break;
      case MENUS.HISTORY:
        Tracker.trackEvent({ event: 'Open Prescription History' });
        break;
      case MENUS.HELP:
        Tracker.trackEvent({ event: 'Help Central' });
        break;
      case MENUS.OFFICE:
        trackingService.trackOfficeMenuSidebarClick();
        break;
      default:
        break;
    }
  }, [prescriberConfiguration, establishmentName]);

  const openSubPage = useCallback(
    (menu: MenuKey) => {
      trackMenuOpen(menu);
      setSelectedMenu(menu);
    },
    [setSelectedMenu, trackMenuOpen]
  );
  const goBack = useCallback(() => setSelectedMenu(''), [setSelectedMenu]);
  const openFreeSample = useCallback(() => setIsFreeSampleOpen(true), []);
  const closeFreeSample = useCallback(() => setIsFreeSampleOpen(false), []);
  const onClickPolicy = useCallback(() => setSelectedMenu(MENUS.POLICY), [setSelectedMenu]);
  const onClickGoBackFromPolicy = useCallback(() => setSelectedMenu(MENUS.DRUG_INTERACTIONS), [setSelectedMenu]);

  const goBackFromInteractions = useCallback(() => {
    const closedDetail = drugInteractionsRef.current?.tryGoBack();
    if (!closedDetail) setSelectedMenu('');
  }, [setSelectedMenu]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) closePanel();
  }, [closePanel]);

  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') closePanel(); };
    if (drawerOpen) document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [drawerOpen, closePanel]);

  const prescriberInitials = prescriber?.Nome?.charAt(0)?.toUpperCase() || 'M';
  const prescriberName = prescriber ? `Dr ${prescriber.Nome}` : '';

  const renderSubPage = () => {
    switch (selectedMenu) {
      case MENUS.PRESCRIBER_DATA:
        return (
          <div className="flex flex-col h-full">
            <SubPageHeader title="Meus dados" onClose={closePanel} />
            <div className="flex-1 min-h-0">
              <PrescriberCentral />
            </div>
            <SubPageFooter onBack={goBack} />
          </div>
        );
      case MENUS.PATIENT_DATA:
        return (
          <div className="flex flex-col h-full">
            <SubPageHeader title="Paciente" onClose={closePanel} />
            <div className="flex-1 min-h-0 overflow-y-auto">
              <PatientData onBack={goBack} />
            </div>
          </div>
        );
      case MENUS.HISTORY:
        return (
          <div className="flex flex-col h-full">
            <SubPageHeader title="Histórico" onClose={closePanel} />
            <div className="flex-1 min-h-0 overflow-y-auto">
              <History />
            </div>
            <SubPageFooter onBack={goBack} />
          </div>
        );
      case MENUS.DRUG_INTERACTIONS:
        return (
          <div className="flex flex-col h-full">
            <SubPageHeader title="Interações" onClose={closePanel} />
            <div className="flex-1 min-h-0 overflow-y-auto">
              <DrugInteractions
                ref={drugInteractionsRef}
                medicinesInteractions={medicineDrugInteractions}
                medicines={medicines || []}
                interactionId={interactionId}
                medicineId={medicineId}
                numberOfInteractions={numberOfInteractions}
                onClickPolicy={onClickPolicy}
                viewByActivePrinciple={viewByActivePrinciple || false}
              />
            </div>
            <SubPageFooter onBack={goBackFromInteractions} />
          </div>
        );
      case MENUS.POLICY:
        return (
          <div className="flex flex-col h-full">
            <SubPageHeader title="Interações" onClose={closePanel} />
            <div className="flex-1 min-h-0 overflow-y-auto">
              <Policy onClickGoBack={onClickGoBackFromPolicy} numberOfInteractions={numberOfInteractions} />
            </div>
            <SubPageFooter onBack={onClickGoBackFromPolicy} />
          </div>
        );
      case MENUS.HELP:
        return (
          <div className="flex flex-col h-full">
            <SubPageHeader title="Ajuda" onClose={closePanel} />
            <div className="flex-1 min-h-0 overflow-y-auto">
              <Help />
            </div>
            <SubPageFooter onBack={goBack} />
          </div>
        );
      case MENUS.OFFICE:
        return (
          <div className="flex flex-col h-full">
            <SubPageHeader title="Mevo App" onClose={closePanel} />
            <div className="flex-1 min-h-0 overflow-y-auto">
              <Office
                qrCodeLink={userSignUpStatus?.data?.url || ''}
                triggerSource={officeTriggerSource}
              />
            </div>
            <SubPageFooter onBack={goBack} />
          </div>
        );
      case MENUS.RECURRENCES:
        return (
          <div className="flex flex-col h-full">
            <SubPageHeader title="Renovações agendadas" onClose={closePanel} />
            <div className="flex-1 min-h-0 overflow-y-auto">
              <Recurrences onBack={goBack} />
            </div>
            <SubPageFooter onBack={goBack} />
          </div>
        );
      default:
        return null;
    }
  };

  if (!drawerOpen) return null;
  if (selectedMenu === MENUS.SNCR_NUMBERING) return null;

  const listContent = (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-lighter shrink-0">
        <p className="text-[16px] font-bold text-ink m-0">Menu</p>
        <CloseBtn onClick={closePanel} />
      </div>

      <div className="flex items-center gap-3 px-4 py-4 shrink-0">
        <div className="w-12 h-12 rounded-full bg-primary-soft border border-primary-border flex items-center justify-center shrink-0">
          <span className="text-primary text-[16px] font-bold">{prescriberInitials}</span>
        </div>
        <div className="min-w-0">
          <p className="text-[16px] font-bold text-ink m-0 truncate">{prescriberName}</p>
          <p className="text-[14px] font-medium text-gray-slate m-0 truncate">
            {prescriber ? `${prescriber.Conselho}: ${prescriber.Numero} | ${prescriber.UFConselho}` : ''}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <p className="px-4 pt-4 pb-2 text-[14px] font-bold text-gray-dark m-0">Prescritor</p>
        <MenuItemRow
          icon={<DoctorIcon style={{ width: 18, height: 18, fill: COLOR_PRIMARY }} />}
          label="Meus dados"
          onClick={() => openSubPage(MENUS.PRESCRIBER_DATA)}
          noDivider={!enableFreeSample && !isSncrNumberingEnabled}
        />
        {enableFreeSample && (
          <MenuItemRow
            icon={<GiftIcon style={{ width: 18, height: 18, fill: 'none', color: COLOR_PRIMARY }} />}
            label="Amostra grátis"
            onClick={openFreeSample}
            noDivider={!isSncrNumberingEnabled}
          />
        )}
        {isSncrNumberingEnabled && (
          <MenuItemRow
            icon={<SncrNumberingIcon style={{ width: 18, height: 18, fill: COLOR_PRIMARY }} />}
            label="Numeração SNCR"
            onClick={() => openSubPage(MENUS.SNCR_NUMBERING)}
            noDivider
          />
        )}

        <p className="px-4 pt-5 pb-2 text-[14px] font-bold text-gray-dark m-0">Paciente</p>
        <MenuItemRow
          icon={<PatientDataIcon style={{ width: 18, height: 18, fill: COLOR_PRIMARY }} />}
          label="Perfil"
          onClick={() => openSubPage(MENUS.PATIENT_DATA)}
        />
        {canUsePrescriptionHistory && (
          <MenuItemRow
            icon={<HistoryIcon style={{ width: 18, height: 18, fill: COLOR_PRIMARY }} />}
            label="Histórico"
            onClick={() => openSubPage(MENUS.HISTORY)}
          />
        )}
        <MenuItemRow
          icon={<InteractionsIcon style={{ width: 18, height: 18, fill: COLOR_PRIMARY }} />}
          label="Interação"
          onClick={() => openSubPage(MENUS.DRUG_INTERACTIONS)}
          badge={numberOfInteractions > 0 ? numberOfInteractions : undefined}
        />
        <MenuItemRow
          icon={<CalendarClockIcon style={{ width: 18, height: 18, color: COLOR_PRIMARY }} />}
          label="Renovações agendadas"
          onClick={() => openSubPage(MENUS.RECURRENCES)}
        />

        {userSignUpStatus?.enabled && (
          <>
            <p className="px-4 pt-5 pb-2 text-[14px] font-bold text-gray-dark m-0">Aplicativo</p>
            <MenuItemRow
              icon={<MevoAppIcon style={{ width: 18, height: 18, fill: COLOR_PRIMARY }} />}
              label="Mevo App"
              onClick={() => openSubPage(MENUS.OFFICE)}
            />
          </>
        )
        }
      </div >

      <div className="shrink-0 px-4 py-4">
        <p className="text-[13px] text-gray-medium text-center m-0 mb-3">Não encontrou o que procurava?</p>
        <button
          onClick={() => openSubPage(MENUS.HELP)}
          className="w-full h-10 flex items-center justify-center gap-2 rounded-[48px] cursor-pointer border-0 transition-colors hover:brightness-95"
          style={{ backgroundColor: 'var(--color-primary-soft)' }}
        >
          <HelpIcon style={{ width: 20, height: 20, fill: COLOR_PRIMARY, color: COLOR_PRIMARY }} />
          <span className="text-[15px] font-bold" style={{ color: COLOR_PRIMARY }}>Ajuda</span>
        </button>
      </div>
    </div >
  );

  const freeSampleSidebar = enableFreeSample ? (
    <FreeSampleSidebar
      isOpen={isFreeSampleOpen}
      onClose={closeFreeSample}
      establishmentOptions={freeSampleEstablishmentOptions}
      prescriber={mapPrescriberInfo(prescriber)}
    />
  ) : null;

  if (isMobile) {
    return (
      <>
        <BottomSheet
          isOpen={drawerOpen}
          onClose={closePanel}
          fullScreen={!!selectedMenu}
          height={appViewportHeight(0.75)}
        >
          {selectedMenu ? renderSubPage() : listContent}
        </BottomSheet>
        {freeSampleSidebar}
      </>
    );
  }

  return (
    <>
      <div
        ref={overlayRef}
        onClick={handleOverlayClick}
        /* Posicionamento com top/right/bottom/left (longhands) em vez da shorthand
           `inset`, que não existe no Chrome 86 (ambiente MV) — sem ela este overlay
           ficava sem dimensões e o painel invisível ao clicar no menu. */
        className="fixed top-0 right-0 bottom-0 left-0 flex justify-end"
        style={{ zIndex: 1300, backgroundColor: 'rgba(0,0,0,0.35)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
      >
        <div
          className="relative bg-white flex flex-col h-full shadow-2xl"
          style={{ width: 'min(420px, 100vw)', animation: 'slideInFromRight 0.25s ease-out' }}
          onClick={(e) => e.stopPropagation()}
        >
          {selectedMenu ? renderSubPage() : listContent}
        </div>
      </div>
      {freeSampleSidebar}
    </>
  );
}
